import BrandLogo from "@/components/BrandLogo";
import { useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle, CheckCircle2, Send, ArrowLeft } from "lucide-react";

export default function RequestProviderAccess() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await API.post("/providers/request-access", { email, name, reason });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't submit your request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-primary/10" data-testid="request-provider-access-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <BrandLogo className="w-56 max-w-full mx-auto mb-4" testId="request-access-logo" />
          <h1 className="text-3xl font-display font-bold">Request Provider Access</h1>
          <p className="text-sm text-muted-foreground mt-1">Elevated Psychiatry</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          {submitted ? (
            <div className="text-center space-y-3 py-4" data-testid="request-submitted">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <p className="font-medium">Request submitted</p>
              <p className="text-sm text-muted-foreground">Dr. Mattox has been notified. You'll receive an email if your request is approved.</p>
              <Link to="/provider-login" className="text-sm text-primary hover:underline inline-block mt-2">Back to provider sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="request-provider-form">
              <div>
                <Label className="text-sm font-medium">Your name</Label>
                <Input data-testid="request-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Dr. Jane Smith" className="mt-1.5 h-12" required />
              </div>
              <div>
                <Label className="text-sm font-medium">Your email</Label>
                <Input data-testid="request-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12" required />
              </div>
              <div>
                <Label className="text-sm font-medium">Why you'd like access (optional)</Label>
                <Textarea data-testid="request-reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={4} placeholder="Tell Dr. Mattox about your role, credentials, and what you'd be doing in the portal." className="mt-1.5" />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <Button data-testid="request-submit" type="submit" className="w-full h-12 rounded-full text-base gap-2" disabled={loading}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Submit request</>}
              </Button>
              <Link to="/provider-login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to provider sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
